import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { BehaviorSubject, Observable } from 'rxjs';
import { AppState } from '@app-core/models/app-state';
import { PageState } from '@app-core/models/_enums';

/* eslint-disable @typescript-eslint/no-explicit-any */

@Injectable({
  providedIn: 'root'
})
export class StateService {
  private _onStateUpdated$ = new BehaviorSubject<AppState>(
    new AppState(PageState.LANDINGS_PAGE)
  );
  private _onLocalStorageError$ = new BehaviorSubject<boolean>(false);

  constructor() {
    this.updateState(this.getState(), false);
  }

  public updateState(
    updatedState: Partial<AppState>,
    updateModifiedDate: boolean = true,
    mergeState: boolean = true
  ): void {
    if (updateModifiedDate) updatedState.modifiedDate = new Date();

    const currentState = { ...this.getState() };
    const stateObject = mergeState
      ? { ...currentState, ...updatedState }
      : updatedState;

    const state = { ...stateObject, version: environment.version };

    try {
      this._onLocalStorageError$.next(false);
      localStorage.setItem(environment.state.key, JSON.stringify(state));
      this._onStateUpdated$.next(state as AppState);
    } catch (error) {
      this._onLocalStorageError$.next(true);
    }
  }

  public getState$(): Observable<AppState> {
    return this._onStateUpdated$.asObservable();
  }

  public hasLocalStorageError$(): Observable<boolean> {
    return this._onLocalStorageError$.asObservable();
  }

  public resetState(): void {
    this.updateState(new AppState(PageState.LANDINGS_PAGE), true, false);
  }

  private getState(): AppState {
    const defaultState = new AppState(PageState.LANDINGS_PAGE, new Date());

    try {
      this._onLocalStorageError$.next(false);
      const state = localStorage.getItem(environment.state.key);

      const currentState = state
        ? (JSON.parse(state) as AppState)
        : defaultState;

      if (currentState.version !== environment.version) {
        return defaultState;
      }

      const timeDiff =
        new Date().getTime() - new Date(currentState.modifiedDate).getTime();

      if (timeDiff >= environment.state.resetTime * 60 * 1000) {
        return defaultState;
      }

      return currentState;
    } catch (error) {
      this._onLocalStorageError$.next(true);
    }

    return defaultState;
  }
}
